import { useState } from 'react'
import PullToRefresh from 'react-simple-pull-to-refresh'
import { useSuggestions, useUpdateSuggestion, useDeleteSuggestion } from '@/hooks/useSuggestions'
import { CreateSuggestionDialog } from '@/components/features/suggestions/CreateSuggestionDialog'
import { SuggestionList } from '@/components/features/suggestions/SuggestionList'
import { SuggestionForm } from '@brent/core-ui/components'
import { searchComposers, getComposerWorks } from '@/services/api/wikidata'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@brent/core-ui/ui'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@brent/core-ui/ui'
import type { Suggestion } from '@/services/api/suggestions'
import type { SuggestionFormData } from '@brent/shared/validation'

export default function Suggestions() {
  const { data: suggestions, isLoading, error, refetch } = useSuggestions()
  const updateMutation = useUpdateSuggestion()
  const deleteMutation = useDeleteSuggestion()

  const [editingSuggestion, setEditingSuggestion] = useState<Suggestion | null>(null)
  const [deletingSuggestion, setDeletingSuggestion] = useState<Suggestion | null>(null)

  const handleEdit = (suggestion: Suggestion) => {
    setEditingSuggestion(suggestion)
  }

  const handleEditSubmit = async (data: SuggestionFormData) => {
    if (!editingSuggestion) return

    try {
      await updateMutation.mutateAsync({
        id: editingSuggestion.suggestion_id,
        input: data,
      })
      setEditingSuggestion(null)
    } catch (error) {
      console.error('Failed to update suggestion:', error)
    }
  }

  const handleDelete = (suggestion: Suggestion) => {
    setDeletingSuggestion(suggestion)
  }

  const handleDeleteConfirm = async () => {
    if (!deletingSuggestion) return

    try {
      await deleteMutation.mutateAsync(deletingSuggestion.suggestion_id)
      setDeletingSuggestion(null)
    } catch (error) {
      console.error('Failed to delete suggestion:', error)
    }
  }

  const handleRefresh = async () => {
    await refetch()
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">My Piece Suggestions</h1>
          <p className="text-muted-foreground mt-2">
            Suggest pieces you'd like to play in future concerts
          </p>
        </div>
        <CreateSuggestionDialog className="w-full sm:w-auto" />
      </div>

      <PullToRefresh
        onRefresh={handleRefresh}
        pullDownThreshold={80}
        maxPullDownDistance={120}
        resistance={2}
      >
        <SuggestionList
          suggestions={suggestions}
          isLoading={isLoading}
          error={error}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </PullToRefresh>

      {/* Edit Dialog */}
      <Dialog
        open={!!editingSuggestion}
        onOpenChange={(open) => !open && setEditingSuggestion(null)}
      >
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Edit Suggestion</DialogTitle>
            <DialogDescription>Update the composer or title of your suggestion.</DialogDescription>
          </DialogHeader>
          {editingSuggestion && (
            <SuggestionForm
              defaultValues={{
                composer: editingSuggestion.composer,
                title: editingSuggestion.title,
                composer_wikidata_id: editingSuggestion.composer_wikidata_id || null,
                work_wikidata_id: editingSuggestion.work_wikidata_id || null,
                comment: editingSuggestion.comment || '',
              }}
              onSubmit={handleEditSubmit}
              onCancel={() => setEditingSuggestion(null)}
              isLoading={updateMutation.isPending}
              submitLabel="Save Changes"
              searchComposers={searchComposers}
              searchWorks={getComposerWorks}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletingSuggestion}
        onOpenChange={(open) => !open && setDeletingSuggestion(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your suggestion for "{deletingSuggestion?.title}" by{' '}
              {deletingSuggestion?.composer}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
